import {
  useState, useEffect, useRef,
} from 'react'
import {
  Link, useNavigate, useSearchParams,
} from 'react-router-dom'
import {
  Loader2,
} from 'lucide-react'
import {
  useAuth,
} from '../hooks/useAuth'
import {
  usePricing,
} from '../hooks/usePricing'
import {
  billingService,
} from '../services/api'
import type {
  SubscriptionResponse,
} from '../services/api'
import {
  CheckoutModal,
} from './CheckoutModal'
import {
  captureReferralFromUrl,
  hasCheckoutIntent,
  clearCheckoutIntent,
} from '../utils/referral'
import {
  isPastDue,
  isTeamCovered,
  PAST_DUE_NOTICE,
  TEAM_COVERED_NOTICE,
  capturePortalIntentFromUrl,
  hasPortalIntent,
  clearPortalIntent,
} from '../utils/billing'

const PRIMARY_CTA_CLASS = 'block w-full text-center bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 shadow-soft hover:shadow-soft-lg disabled:opacity-50 active:scale-[0.96]'

const CheckIcon = () => (
  <svg className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
)

export function PricingPage(): JSX.Element {
  const { isAuthenticated } = useAuth()
  const { pricing } = usePricing()
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null)
  // Explicit confirmation gate for the ?upgrade=1 "end my trial, start paying"
  // flow: a carded trialing user is charged immediately with no hosted checkout,
  // so we ask before charging rather than doing it silently on page load.
  const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoCheckoutTriggered = useRef(false)
  const autoPortalTriggered = useRef(false)

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true)
      billingService.getSubscription()
        .then(setSubscription)
        .catch((err) => console.error('Failed to load subscription:', err))
        .finally(() => setLoading(false))
    }
  }, [isAuthenticated])

  const handleCheckout = async (convert = false) => {
    setCheckoutLoading(true)
    try {
      const {
        client_secret, converted, payment_failed, portal_url,
      } = await billingService.createCheckoutSession(
        `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        { convert },
      )
      setUpgradeConfirmOpen(false)
      // A carded trialing user was converted in place (charged now, no hosted
      // checkout) — route straight to the success screen.
      if (converted) {
        navigate('/billing/success')
        return
      }
      // Converting needs a card first: the portal is where they add one, then
      // its return_url lands them back on /pricing?upgrade=1 to convert.
      if (portal_url) {
        window.location.href = portal_url
        return
      }
      // The convert charge declined and the subscription is now past_due —
      // re-fetch so the page's existing payment-repair UI takes over.
      if (payment_failed) {
        const sub = await billingService.getSubscription()
        setSubscription(sub)
        return
      }
      // Otherwise open the Stripe checkout to collect a card / confirm payment.
      if (client_secret) {
        setCheckoutClientSecret(client_secret)
      } else {
        console.error('Checkout returned neither a client_secret nor a conversion')
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error)
    } finally {
      setCheckoutLoading(false)
    }
  }

  // Capture referral / portal params on entry (covers client-side navigation
  // to /pricing?ref=... or /pricing?portal=true after App's module-level
  // capture has already run).
  useEffect(() => {
    captureReferralFromUrl()
    capturePortalIntentFromUrl()
  }, [])

  // Auto-open checkout when arriving via a referral / ?checkout=true link, or
  // via the assistant's ?upgrade=1 "start paying / end my trial" deep link.
  // Fires once per mount; the discount is read from stored referral state.
  useEffect(() => {
    if (autoCheckoutTriggered.current) return
    // ?upgrade=1 = convert now: a trialing user (who is technically active)
    // wants to start paying, so it must open checkout even when is_active.
    const wantsUpgrade = searchParams.get('upgrade') === '1'
    const wantsCheckout
      = searchParams.get('checkout') === 'true' || hasCheckoutIntent() || wantsUpgrade
    if (!wantsCheckout) return

    if (!isAuthenticated) {
      // Route through login first; the referral code + intent persist in web
      // storage across the OAuth round-trip.
      autoCheckoutTriggered.current = true
      navigate('/login', { replace: true })
      return
    }
    // Wait until the subscription status is actually loaded. `loading` can't be
    // used here: it inits to false and the fetch effect's setLoading(true) isn't
    // visible to this closure on the same mount, so we'd fire before the status
    // is known and bounce an already-subscribed user off a 409. `subscription`
    // stays null until the fetch resolves.
    if (subscription === null) return

    autoCheckoutTriggered.current = true
    clearCheckoutIntent()
    // past_due is inactive but must NOT open checkout (see utils/billing.ts).
    if (isPastDue(subscription)) return
    // An organization already pays this seat: the API 409s personal checkout,
    // and ?upgrade=1 must not offer to convert a team-covered trial — doing so
    // minted a second subscription while the org kept paying the seat.
    if (isTeamCovered(subscription)) return
    // A trialing user explicitly upgrading (?upgrade=1) converts by ending the
    // trial and charging now, so gate it behind an explicit confirmation rather
    // than charging on page load. A genuinely paying active user is left alone.
    const isTrial = subscription.status === 'trialing'
    if (wantsUpgrade && isTrial) {
      setUpgradeConfirmOpen(true)
      return
    }
    // Open checkout for users without an active subscription (the hosted
    // checkout is itself the payment confirmation).
    if (!subscription.is_active) {
      void handleCheckout(wantsUpgrade)
    }
  }, [isAuthenticated, subscription, searchParams, navigate, handleCheckout])

  const handleManageSubscription = async () => {
    setPortalLoading(true)
    try {
      const { url } = await billingService.createPortalSession(
        `${window.location.origin}/integrations`,
      )
      window.location.href = url
    } catch (error) {
      console.error('Failed to create portal session:', error)
      setPortalLoading(false)
    }
  }

  // Auto-open the Stripe portal when arriving via a dunning email's
  // /pricing?portal=true card-update deep link. Mirrors the auto-checkout
  // effect above: the intent persists in session storage across the OAuth
  // round-trip, and we wait for the subscription status before acting.
  useEffect(() => {
    if (autoPortalTriggered.current) return
    const wantsPortal
      = searchParams.get('portal') === 'true' || hasPortalIntent()
    if (!wantsPortal) return

    if (!isAuthenticated) {
      autoPortalTriggered.current = true
      navigate('/login', { replace: true })
      return
    }
    if (subscription === null) return

    autoPortalTriggered.current = true
    clearPortalIntent()
    // Only forward users who have a subscription to repair or manage; without
    // one the portal has nothing to fix, so the page's subscribe CTA is the
    // right path.
    if (subscription.is_active || isPastDue(subscription)) {
      void handleManageSubscription()
    }
  }, [isAuthenticated, subscription, searchParams, navigate, handleManageSubscription])

  const renderProfessionalButton = () => {
    if (!isAuthenticated) {
      return (
        <Link
          to="/login"
          className="block w-full text-center bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 shadow-soft hover:shadow-soft-lg active:scale-[0.96]"
        >
          Get Started
        </Link>
      )
    }

    if (loading) {
      return (
        <div className="flex justify-center py-3">
          <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
        </div>
      )
    }

    // Checked before is_active: a coverage row is active, so this would
    // otherwise render "Current Plan" over a Manage Subscription button that
    // opens a personal portal the user has no billing in (GET-10264). Mirrors
    // the Team plan card in BillingSection.
    if (isTeamCovered(subscription)) {
      return (
        <div className="space-y-3">
          <div className="text-center">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-primary-100 text-primary-700 rounded-full">
              Team plan
            </span>
          </div>
          <p className="text-center text-sm text-gray-600">
            {TEAM_COVERED_NOTICE}
          </p>
          {/* The admin's route to the team card lives here, since this branch
              drops the personal portal button. The dunning deep link
              (?portal=true) still forwards admins straight to the org portal
              via the effect above — don't gate that on team coverage. */}
          <Link
            to="/team"
            className="block w-full text-center bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-[background-color,box-shadow,transform] duration-200 border-2 border-primary-200 active:scale-[0.96]"
          >
            View your team
          </Link>
        </div>
      )
    }

    if (subscription?.is_active) {
      return (
        <div className="space-y-3">
          <div className="text-center">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-green-100 text-green-700 rounded-full">
              Current Plan
            </span>
          </div>
          <button
            onClick={() => void handleManageSubscription()}
            disabled={portalLoading}
            className="block w-full text-center bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-[background-color,box-shadow,transform] duration-200 border-2 border-primary-200 disabled:opacity-50 active:scale-[0.96]"
          >
            {portalLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Manage Subscription'
            )}
          </button>
        </div>
      )
    }

    // Past-due subscriptions are repaired via the portal, never re-purchased
    // (see utils/billing.ts).
    if (isPastDue(subscription)) {
      return (
        <div className="space-y-3">
          <p className="text-center text-sm text-gray-600">
            {PAST_DUE_NOTICE}
          </p>
          <button
            onClick={() => void handleManageSubscription()}
            disabled={portalLoading}
            className={PRIMARY_CTA_CLASS}
          >
            {portalLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Update Payment Method'
            )}
          </button>
        </div>
      )
    }

    return (
      <button
        onClick={() => void handleCheckout()}
        disabled={checkoutLoading}
        className={PRIMARY_CTA_CLASS}
      >
        {checkoutLoading ? (
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        ) : (
          'Get Started'
        )}
      </button>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Choose the plan that works best for you. Start with a 10-day free trial.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-soft-lg p-8 border-2 border-primary-200 relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-primary-500 text-white text-sm font-medium px-4 py-1 rounded-full">
              Most Popular
            </span>
          </div>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Professional</h2>
            <p className="text-gray-600">For individuals and small teams</p>
          </div>
          <div className="text-center mb-6">
            <span className="text-5xl font-bold text-gray-900">{pricing?.formatted ?? '...'}</span>
            <span className="text-gray-600">/{pricing?.interval ?? 'month'}</span>
          </div>
          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Conflict detection & smart rescheduling</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Handles scheduling back-and-forth for you</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Inbox triage, email drafts & follow-ups</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Slack, WhatsApp, SMS, iMessage & email</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Learns your preferences over time</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Works 24/7 across time zones</span>
            </li>
          </ul>
          {renderProfessionalButton()}
        </div>

        <div className="bg-white rounded-2xl shadow-soft-lg p-8 border border-gray-200">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h2>
            <p className="text-gray-600">For larger organizations</p>
          </div>
          <div className="text-center mb-6">
            <span className="text-3xl font-bold text-gray-900">Custom Pricing</span>
          </div>
          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Everything in Professional</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Dedicated account manager</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Custom integrations</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Advanced security features</span>
            </li>
            <li className="flex items-start">
              <CheckIcon />
              <span className="text-gray-700">Priority support</span>
            </li>
          </ul>
          <a
            href="mailto:sales@catchagent.ai"
            className="block w-full text-center bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-primary-50 transition-[background-color,box-shadow,transform] duration-200 border-2 border-primary-200 active:scale-[0.96]"
          >
            Contact Sales
          </a>
        </div>
      </div>

      <div className="mt-12 text-center">
        <div className="bg-primary-50 rounded-2xl p-6 max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            How it works
          </h3>
          <p className="text-gray-700">
            Sign up and start your 10-day free trial. After the trial, you&apos;ll be charged {pricing?.formatted ?? '...'}/{pricing?.interval ?? 'month'}.
            You can cancel anytime from your subscription settings.
          </p>
        </div>
      </div>

      <div className="text-center mt-12 pt-8 border-t border-gray-200">
        <p className="text-gray-600 mb-4">Questions about pricing?</p>
        <a
          href="mailto:support@catchagent.ai"
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Contact us at support@catchagent.ai
        </a>
      </div>

      {upgradeConfirmOpen && (
        <UpgradeConfirmModal
          price={pricing?.formatted}
          interval={pricing?.interval}
          loading={checkoutLoading}
          onConfirm={() => void handleCheckout(true)}
          onClose={() => setUpgradeConfirmOpen(false)}
        />
      )}

      {checkoutClientSecret && (
        <CheckoutModal
          clientSecret={checkoutClientSecret}
          onClose={() => setCheckoutClientSecret(null)}
        />
      )}
    </div>
  )
}

interface UpgradeConfirmModalProps {
  price: string | undefined
  interval: string | undefined
  loading: boolean
  onConfirm: () => void
  onClose: () => void
}

function UpgradeConfirmModal({
  price, interval, loading, onConfirm, onClose,
}: UpgradeConfirmModalProps): JSX.Element {
  // Pricing may still be loading — omit the amount rather than misrender.
  const priceSuffix = price ? ` at ${price}/${interval ?? 'month'}` : ''
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={loading ? undefined : onClose}
      />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl m-4 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Start your subscription
        </h2>
        <p className="text-gray-600 mb-6">
          This ends your free trial and starts your paid plan today{priceSuffix}.
          You&apos;ll unlock flight and hotel booking right away.
        </p>
        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <button
            onClick={onConfirm}
            disabled={loading}
            className={PRIMARY_CTA_CLASS}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Start subscription'
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="block w-full text-center bg-white text-gray-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors border border-gray-200 disabled:opacity-50"
          >
            Not yet
          </button>
        </div>
      </div>
    </div>
  )
}
